<template>
  <b-modal
    id="demo-vehicle-lookup-modal"
    title="Tìm kiếm xe demo"
    size="lg"
    hide-footer
    @hidden="onHidden"
  >
    <b-row>
      <b-col>
        <b-form-group label="Mã/Tên sản phẩm">
          <b-form-input
            v-model="searchParams.productName"
            placeholder="Nhập mã hoặc tên sản phẩm"
            @keyup.enter="onSearch"
          ></b-form-input>
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group label="Số khung">
          <b-form-input
            v-model="searchParams.chassisNumber"
            placeholder="Nhập số khung"
            @keyup.enter="onSearch"
          ></b-form-input>
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group label="Biển số">
          <b-form-input
            v-model="searchParams.licensePlate"
            placeholder="Nhập biển số"
            @keyup.enter.prevent="onSearch"
          ></b-form-input>
        </b-form-group>
      </b-col>
    </b-row>

    <b-row class="mb-5">
      <b-col md="6" class="d-flex align-items-end">
        <b-button variant="primary" @click.prevent="onSearch" class="mr-2">
          <i class="flaticon2-search-1"></i> Tìm kiếm
        </b-button>
        <b-button variant="secondary" @click.prevent="handleResetFilter">
          <i class="flaticon2-refresh-button"></i> Làm mới
        </b-button>
      </b-col>
    </b-row>

    <b-table
      :items="vehicles"
      :fields="fields"
      class="table-bordered table-hover"
      :small="true"
      :busy="isLoading"
      selectable
      select-mode="single"
      @row-clicked="onSelectDemoVehicle"
    >
      <template v-slot:table-busy>
        <div class="text-center my-2">
          <b-spinner class="align-middle"></b-spinner>
        </div>
      </template>

      <template v-slot:cell(productName)="row">
        <div class="d-flex flex-column">
          <span class="font-weight-bold">{{ row.item.productCode }}</span>
          <small>{{ row.item.productName }}</small>
        </div>
      </template>

      <!-- Status Cell -->
      <template v-slot:cell(status)="row">
        <div class="text-center">
          <span
            v-text="getStatusConfig(row.item.productChassis.status).text"
            class="label font-weight-bold label-lg label-inline"
            :class="getStatusConfig(row.item.productChassis.status).class"
            style="width: max-content"
          ></span>
        </div>
      </template>

      <template v-slot:empty>
        <div class="text-center py-3">
          <p class="mb-0">Không tìm thấy xe demo nào</p>
        </div>
      </template>
    </b-table>

    <!-- Pagination -->
    <b-row>
      <b-col>
        <p class="mt-3 text-dark" style="font-weight: 500">
          Tổng số:
          {{ convertPrice(paginations.total) }}
        </p>
      </b-col>
      <b-col>
        <b-pagination
          v-if="paginations.totalPages >= 2"
          class="custom-pagination"
          v-model="paginations.currentPage"
          :total-rows="paginations.total"
          :per-page="LIMIT_PAGE"
          @change="fetchDemoTickets"
          align="right"
          first-class="page-item-first btn btn-icon btn-sm m-1"
          prev-class="page-item-prev btn btn-icon btn-sm m-1"
          next-class="page-item-next btn btn-icon btn-sm m-1"
          last-class="page-item-last btn btn-icon btn-sm m-1"
          page-class="btn btn-icon btn-sm border-0 m-1"
        >
          <template v-slot:first-text>
            <span>
              <i class="ki ki-double-arrow-back icon-xs"></i>
            </span>
          </template>

          <template v-slot:prev-text>
            <i class="ki ki-arrow-back icon-xs"></i>
          </template>

          <template v-slot:next-text>
            <i class="ki ki-arrow-next icon-xs"></i>
          </template>

          <template v-slot:last-text>
            <span class="text-info">
              <i class="ki ki-double-arrow-next icon-xs"></i>
            </span>
          </template>
        </b-pagination>
      </b-col>
    </b-row>
  </b-modal>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { convertPrice } from '@/utils/common';
import ProductChassisModel from '../../../posm/models/ProductChassis.model';

export default {
  name: 'DemoVehicleLookupModal',
  props: {
    storeId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      fields: [
        {
          key: 'productName',
          label: 'Sản phẩm',
          sortable: false,
          thStyle: { textAlign: 'center', color: 'rgb(24, 28, 50)' },
        },
        {
          key: 'chassisNumber',
          label: 'Số khung',
          sortable: false,
          thStyle: { textAlign: 'center', color: 'rgb(24, 28, 50)' },
        },
        {
          key: 'licensePlate',
          label: 'Biển số',
          sortable: false,
          thStyle: { textAlign: 'center', color: 'rgb(24, 28, 50)' },
        },
        {
          key: 'currentKm',
          label: 'Số km hiện tại',
          sortable: false,
          thStyle: { textAlign: 'center', color: 'rgb(24, 28, 50)' },
        },
        {
          key: 'status',
          label: 'Trạng thái',
          sortable: false,
          thStyle: { textAlign: 'center', color: 'rgb(24, 28, 50)' },
        },
      ],

      //   States
      isLoading: false,

      // Search
      searchParams: {
        productName: '',
        chassisNumber: '',
        licensePlate: '',
        page: 1,
        limit: this.LIMIT_PAGE,
      },
      // Data
      vehicles: [],

      // Paginations
      LIMIT_PAGE: 4,
      paginations: {
        total: 0,
        totalPages: 0,
        currentPage: 1,
      },
    };
  },

  computed: {
    ...mapGetters({
      error: 'demoCars/getError',
    }),

    fromNo() {
      return (this.paginations.currentPage - 1) * this.LIMIT_PAGE + 1;
    },

    toNo() {
      return Math.min(
        this.LIMIT_PAGE * this.paginations.currentPage,
        this.paginations.totalItems,
      );
    },
  },

  methods: {
    ...mapActions({
      lookupDemoTickets: 'testDrives/lookupDemoTickets',
    }),

    convertPrice,

    async fetchDemoTickets(page = 1) {
      try {
        this.isLoading = true;

        this.searchParams.page = Number(page) || 1;
        this.searchParams.limit = this.LIMIT_PAGE;

        const params = {
          ...this.searchParams,
          storeId: this.storeId,
        };

        const { demoTickets, paginations } = await this.lookupDemoTickets(
          params,
        );
        this.vehicles = demoTickets;
        this.paginations = paginations;
      } catch (error) {
        console.error('Error searching demo vehicles:', error);
        this.$emit('error', 'Không thể tải danh sách xe demo');
      } finally {
        this.isLoading = false;
      }
    },

    async onSearch() {
      await this.fetchDemoTickets();
    },

    reset() {
      this.searchParams = {
        productName: '',
        chassisNumber: '',
        licensePlate: '',
      };
      this.vehicles = [];
    },

    onSelectDemoVehicle(item) {
      this.$emit('select', item);
      this.close();
    },

    close() {
      this.$bvModal.hide('demo-vehicle-lookup-modal');
    },

    onHidden() {
      this.reset();
      this.$emit('hidden');
    },

    async show() {
      this.reset();
      this.$bvModal.show('demo-vehicle-lookup-modal');
      await this.fetchDemoTickets();
    },

    async handleResetFilter() {
      this.reset();
      await this.fetchDemoTickets();
    },

    getStatusConfig(status) {
      return ProductChassisModel.getStatusConfig(status);
    },
  },
};
</script>

<style lang="scss" scoped></style>
